import { NavLink, useLocation } from 'react-router-dom';
import { HashLink } from 'react-router-hash-link';
import { isUrlActive } from '@/utils/isUrlActive';
import { useIsMobile } from '@/hooks/useIsMobile';
import { ReactNode, useEffect, useState } from 'react';

interface NavigationLinkProps {
  path: string;
  children: ReactNode;
  hash?: string;
  onClick?: () => void;
}

function NavigationLink({ path, hash, children, onClick }: NavigationLinkProps) {
  const location = useLocation();
  const isMobile = useIsMobile();

  const searchParams = location.search;
  const fullPath = hash ? `${path}${searchParams}${hash}` : `${path}${searchParams}`;
  const [isActive, setIsActive] = useState(() => isUrlActive(fullPath, location));

  function updateActiveState() {
    const isCurrentlyActive = isUrlActive(fullPath, location);
    setIsActive(isCurrentlyActive);
  }

  useEffect(() => {
    updateActiveState();
  }, [location]);

  const className = `min-w-max block py-2 px-4 transition-all duration-600 rounded-md desktopNav:border-transparent desktopNav:font-[600] desktopNav:rounded-none desktopNav:p-2 desktopNav:px-0 desktopNav:py-2 ${isActive ? 'bg-gray-200 desktopNav:bg-transparent desktopNav:text-shadow-bold desktopNav:gradient-border-bottom' : 'desktopNav:hover:text-teal-600'}`;

  function scrollToElementWithOffset(element: HTMLElement) {
    const headerHeight = isMobile ? 64 : 72;
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - headerHeight;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth',
    });
  }

  return hash ? (
    <HashLink
      to={fullPath}
      onClick={onClick}
      className={className}
      data-cy='navigation-link'
      scroll={scrollToElementWithOffset}
    >
      {children}
    </HashLink>
  ) : (
    <NavLink to={fullPath} onClick={onClick} className={className} data-cy='navigation-link'>
      {children}
    </NavLink>
  );
}

export default NavigationLink;
